import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarDays, ExternalLink } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Trust } from "../components/home/Trust";
import { useLang } from "../i18n/LanguageContext";
import { getCaseStudy } from "../data/caseStudies";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const cs = getCaseStudy(slug);

  if (!cs) {
    return (
      <main data-testid="case-detail-missing" className="mx-auto max-w-3xl px-5 pt-40 pb-24 text-center">
        <h1 className="font-display text-3xl font-bold text-sinovar-navy">404</h1>
        <Link to="/case-studies" className="mt-4 inline-block text-sinovar-blue font-semibold">{t.nav.cases}</Link>
      </main>
    );
  }

  return (
    <main data-testid="case-detail-page">
      <section className="bg-gradient-to-b from-sinovar-soft to-white pt-32 md:pt-40 pb-12">
        <div className="mx-auto max-w-4xl px-5 md:px-8">
          <Link to="/case-studies" data-testid="case-back" className="inline-flex items-center gap-2 text-sm font-semibold text-sinovar-navy/60 hover:text-sinovar-navy">
            <ArrowLeft className="h-4 w-4" /> {t.nav.cases}
          </Link>
          <div className="mt-6 font-display text-sm font-bold tracking-[0.2em] text-sinovar-navy/50">{cs.brand}</div>
          <h1 className="mt-4 font-display text-4xl md:text-5xl font-bold tracking-tight text-sinovar-navy leading-[1.05]">
            {cs.title[lang]}
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-sinovar-navy/65 leading-relaxed">{cs.excerpt[lang]}</p>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-medium text-sinovar-navy/70">
            <span className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-sinovar-blue" /> {cs.since[lang]}</span>
            <span>{cs.tag[lang]}</span>
            {cs.website && (
              <a href={`https://${cs.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sinovar-blue" data-testid="case-website">
                {cs.website} <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        </div>
      </section>

      <section className="bg-white pb-4">
        <div className="mx-auto max-w-5xl px-5 md:px-8">
          <div className="overflow-hidden rounded-2xl shadow-xl shadow-sinovar-navy/10">
            <img src={cs.image} alt={cs.brand} className="w-full object-cover max-h-[440px]" />
          </div>
        </div>
      </section>

      <section className="bg-white py-14 md:py-20">
        <div className="mx-auto max-w-3xl px-5 md:px-8 space-y-12">
          {cs.story[lang].map((s, i) => (
            <Reveal key={i} delay={i * 0.03}>
              <div className="border-l-2 border-sinovar-red/30 pl-6">
                <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight text-sinovar-navy">{s.h}</h2>
                <p className="mt-3 text-base md:text-lg text-sinovar-navy/70 leading-relaxed">{s.p}</p>
              </div>
            </Reveal>
          ))}

          <div className="flex flex-wrap gap-2 border-t border-black/[0.06] pt-8">
            {cs.services[lang].map((s) => (
              <span key={s} className="rounded-full border border-black/10 bg-sinovar-soft px-3.5 py-1.5 text-sm font-medium text-sinovar-navy/70">{s}</span>
            ))}
          </div>

          <Link to="/case-studies" className="group inline-flex items-center gap-2 text-[15px] font-semibold text-sinovar-blue" data-testid="case-more">
            {t.pages.cases.title} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <Trust />
    </main>
  );
}
